import Button from '@allesauto/core/button';
import CircularLoader, {
  CircularLoaderSize,
  CircularLoaderType,
} from '@allesauto/core/circularLoader';
import InputField from '@allesauto/core/inputField';
import LoaderOverlay from '@allesauto/core/loaderOverlay';
import PasswordInput from '@allesauto/core/passwordInput';
import SecondaryButton from '@allesauto/core/secondaryButton';
import TextLink from '@allesauto/core/textLink';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { consts, Routes_DE as Routes } from '@allesauto/utils/consts';
import { isAuregoShop } from '@allesauto/utils/funcs.ts';
import { ArrowRight16 } from '@carbon/icons-react';
import { getIn, useFormik } from 'formik';
import isArray from 'lodash/isArray';
import { useRouter } from 'next/router';
import React, {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { signIn } from '@/global/auth/auth';
import { loginAndSaveToken, LoginToken } from '@/store/redux/actions/user';
import { selectTradeInState } from '@/store/redux/slices/pages/tradein';

import AccountFormWrapper from '../AccountFormWrapper';
import loginSchema from '../formValidation/loginSchema';

export interface LoginProps {
  title: string;
  emailLabel: string;
  emailPlaceholder?: string;
  passwordLabel: string;
  passwordPlaceholder?: string;
  loginButtonText: string;
  forgottenPasswordCtaText: string;
  merchantPortalCtaText: string;
  createAccountTitle: string;
  registerButtonText: string;
  secureConnectionText: string;
}
type loadingStatus = 'loginBtn' | 'registerBtn' | 'forgotBtn' | undefined;

const Login: FC<React.PropsWithChildren<LoginProps>> = ({
  title,
  emailLabel,
  emailPlaceholder = ' ',
  passwordLabel,
  passwordPlaceholder = ' ',
  loginButtonText,
  forgottenPasswordCtaText,
  merchantPortalCtaText,
  createAccountTitle,
  registerButtonText,
  secureConnectionText,
}) => {
  const intl = useIntl();
  const router = useRouter();
  const dispatch = useDispatch();
  const tradeInState = useSelector(selectTradeInState);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [showFormError, setShowFormError] = useState<boolean>(false);
  const [showSignInError, setShowSignInError] = useState<boolean>(false);
  const [loading, setLoading] = useState<loadingStatus>();
  useState<boolean>(false);

  let showConfirmMessage = false;
  const { confirmedUser, resetPassword } = router.query;

  if (confirmedUser || resetPassword) {
    showConfirmMessage = true;
  }

  const linkHandler = useCallback(
    (value: loadingStatus) => () => {
      setLoading(value);
    },
    []
  );

  const pushLoginInfoToDataLayer = () => {
    // dataLayer push after successfully signing in
    pushAnalyticsEvent({
      event: 'login',
      login_status: 'logged-in',
      // TODO: replace with customer id
      customer_id: '',
      // TODO: we cannot differentiate yet between user_type: [dealer, customer]
      user_type: 'customer',
    });
  };

  const validationSchema = useMemo(() => {
    const schema = {
      email: {
        format: translate(intl, 'ui.FORMS.USER_ACCOUNT.EMAIL_INVALID_MSG'),
        required: translate(intl, 'ui.FORMS.USER_ACCOUNT.EMAIL_INVALID_MSG'),
      },
      password: {
        required: translate(intl, 'ui.FORMS.USER_ACCOUNT.REQUIRED_MSG'),
      },
    };

    return loginSchema(schema);
  }, [intl]);

  const whitelabel = useWhiteLabel();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: (formData: { email: string; password: string }) => {
      setLoading('loginBtn');
      signIn({
        email: formData.email,
        password: formData.password,
        merchantUuids: whitelabel?.merchantUuids,
        merchantOrigin: String(whitelabel?.merchantOrigin),
      })
        .then(async (response: LoginToken | null) => {
          if (response) {
            dispatch(loginAndSaveToken(response));
            pushLoginInfoToDataLayer();

            const redirectTo = router.query.redirectTo
              ? isArray(router.query.redirectTo)
                ? router.query.redirectTo[0]
                : router.query.redirectTo
              : Routes.ACCOUNT.GARAGE;

            await router.push(decodeURIComponent(redirectTo));
            setLoading(undefined);
          }
        })
        .catch(() => {
          setShowSignInError(true);
          setLoading(undefined);
        });
    },
    validationSchema: validationSchema,
  });

  const handleButtonToRegister = async () => {
    setLoading('registerBtn');

    if (router.query.redirectTo === Routes.TRADE_IN_STEPS.DEALER) {
      await router.push(
        `${Routes.ACCOUNT.REGISTER}?redirectTo=${encodeURIComponent(
          Routes.TRADE_IN_STEPS.DEALER
        )}`
      );

      localStorage.setItem('tradeIn', JSON.stringify(tradeInState));
    } else {
      await router.push(Routes.ACCOUNT.REGISTER);
    }

    setLoading(undefined);
  };

  const getEmailErrorMessage = (): string => {
    if (formik.touched.email) return getIn(formik.errors, 'email');
    // Highlight field, error will be displayed below submit button
    else if (showFormError) return ' ';
    return '';
  };

  const getPasswordErrorMessage = (): string => {
    if (
      (showFormError && !formik.touched.password) ||
      (formik.dirty && showFormError)
    ) {
      // Highlight field, error will be displayed below submit button
      return ' ';
    }
    return '';
  };

  useEffect(() => {
    if (formik.dirty && formik.isValid) {
      setShowFormError(false);
      setIsFormValid(true);
      return;
    }

    setIsFormValid(false);
  }, [formik.dirty, formik.isValid]);

  const submitHandler = () => {
    if (!formik.dirty) {
      setShowFormError(true);
      return;
    }

    if (isFormValid) {
      formik.submitForm();
      return;
    }

    setShowFormError(true);
    formik.validateForm();
  };

  const handleKeypress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      submitHandler();
    }
  };

  return (
    <>
      {loading !== undefined && (
        <LoaderOverlay
          customClassName={{
            containerCustomClass: 'fixed top-[70px] h-screen w-screen',
          }}
          hasLoader={false}
          hasZeroOpacity
        />
      )}
      <AccountFormWrapper
        title={title}
        secureConnectionText={secureConnectionText}
      >
        {showConfirmMessage && (
          <p className="mb-20px text-functional03 text-p3">
            {confirmedUser && translate(intl, 'ui.FORMS.LOGIN.CONFIRM_MESSAGE')}
            {resetPassword &&
              translate(intl, 'ui.FORMS.LOGIN.RESET_PASSWORD_MESSAGE')}
          </p>
        )}
        <form>
          <InputField
            name="email"
            label={emailLabel}
            placeholder={emailPlaceholder}
            customStyle="mb-30px"
            type="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
            errorMessage={getEmailErrorMessage()}
            autoComplete="email"
            id="input--email"
            required
          />
          <PasswordInput
            name="password"
            label={passwordLabel}
            placeholder={passwordPlaceholder}
            customStyle="mb-30px"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.password}
            errorMessage={getPasswordErrorMessage()}
            autoComplete="current-password"
            id="input--current-password"
            onKeyPress={handleKeypress}
          />

          {showFormError && (
            <span className="block leading-4 mb-10px mt-20px text-p3 text-red60">
              {translate(intl, 'ui.FORMS.USER_ACCOUNT.REQUIRED_MSG')}
            </span>
          )}
          {showSignInError && (
            <span className="block leading-4 mb-10px mt-20px text-p3 text-red60">
              {translate(intl, 'ui.FORMS.LOGIN.WRONG_LOGIN_ERROR')}
            </span>
          )}

          <Button fullWidth whitelabel={true} onClick={submitHandler}>
            {loading === 'loginBtn' ? (
              <CircularLoader
                type={
                  isAuregoShop(whitelabel)
                    ? CircularLoaderType.secondary
                    : CircularLoaderType.primary
                }
                customSize={CircularLoaderSize.sm}
              />
            ) : (
              loginButtonText
            )}
          </Button>
        </form>
        <div className="flex mb-60px mt-20px">
          <TextLink
            href={Routes.ACCOUNT.PASSWORD}
            isLoading={loading === 'forgotBtn'}
            text={forgottenPasswordCtaText}
            customClassName="mr-20px"
            icon={<ArrowRight16 className="-mt-[1px] h-5 w-5" />}
            onClick={linkHandler('forgotBtn')}
          />
          {!whitelabel && (
            <TextLink
              href={consts.MERCHANT_PORTAL_URL}
              text={merchantPortalCtaText}
              target="_blank"
              icon={<ArrowRight16 className="-mt-[1px] h-5 w-5" />}
            />
          )}
        </div>
        <h2 className="font-headline font-weight-bold mb-10px text-p2">
          {createAccountTitle}
        </h2>
        <SecondaryButton
          customClassName="mb-100px md:mb-30px wl-custom-text-highlight"
          onClick={handleButtonToRegister}
          fullWidth
        >
          {loading === 'registerBtn' ? (
            <CircularLoader
              type={CircularLoaderType.primary}
              customSize={CircularLoaderSize.sm}
            />
          ) : (
            registerButtonText
          )}
        </SecondaryButton>
      </AccountFormWrapper>
    </>
  );
};

export default Login;
